import { useEffect, useRef, useState } from "react";
import {
  enviarComprovante,
  lerMeuPagamento,
  type MeuPagamento,
} from "../lib/faculdade";

/**
 * O popup de pagamento da taxa, e o comprovante que o aluno anexa (000242).
 *
 * Pedido de Victor em 16/09/2026: *"devemos disponibilizar um popup pra ele
 * anexar o comprovante de pagamento. Até que ele anexe o comprovante, o status
 * continua Aguardando Pagamento; quando ele anexar, muda para Em análise para
 * que a secretaria valide manualmente. A secretaria tem o poder de recusar o
 * comprovante e o status volta pra Aguardando Pagamento"*.
 *
 * ── `<dialog>` nativo, e ele usa token SEMÂNTICO ──────────────────────────
 *
 * `<dialog>` traz Esc, armadilha de foco, `aria-modal` e backdrop de graça — as
 * quatro coisas que uma `div` com overlay exige reimplementar e que costumam
 * ficar pela metade. E o `m-auto` NÃO é enfeite: o reset da casa zera a margem
 * de tudo, e sem ele o `inset: 0` do navegador resolve para o CANTO superior
 * esquerdo (a cicatriz do modal do Mapa de Concursos).
 *
 * As cores são semânticas, ao contrário do card que abre este popup: o card vive
 * dentro de `painel-navy`, onde token semântico é armadilha; o `<dialog>` é
 * desenhado na top layer, fora do território, e ali quem manda é o tema.
 *
 * ── O que a tela NÃO decide ───────────────────────────────────────────────
 *
 * Se cobra, quanto e por qual link: tudo vem de `sec_meu_pagamento`. A regra é
 * do par (tipo, instituição) e mora em `sec_taxas` — uma segunda conta aqui
 * divergiria no dia em que a FBC passasse a cobrar, e o aluno pagaria por algo
 * que o banco considera gratuito (ou o contrário).
 */
export default function ModalPagamento({
  documentoId,
  nomeDoTipo,
  aberto,
  aoFechar,
  aoMudar,
}: {
  documentoId: string;
  nomeDoTipo: string;
  aberto: boolean;
  aoFechar: () => void;
  /** Chamado quando o comprovante entra: a tela de fora recarrega o status. */
  aoMudar: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [dados, setDados] = useState<MeuPagamento | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (aberto && !dlg.open) dlg.showModal();
    if (!aberto && dlg.open) dlg.close();
  }, [aberto]);

  // O estado é lido a cada abertura, não uma vez na montagem: entre duas
  // aberturas a secretaria pode ter recusado o comprovante, e um popup que
  // mostra o estado da abertura anterior manda o aluno anexar de novo o que
  // já está em análise.
  useEffect(() => {
    if (!aberto) return;
    let vivo = true;
    setCarregando(true);
    setErro(null);
    setArquivo(null);
    lerMeuPagamento(documentoId).then((d) => {
      if (!vivo) return;
      setDados(d);
      setCarregando(false);
    });
    return () => {
      vivo = false;
    };
  }, [aberto, documentoId]);

  async function mandar() {
    if (!arquivo) {
      setErro("Escolha o arquivo do comprovante.");
      return;
    }
    setEnviando(true);
    setErro(null);
    const r = await enviarComprovante(documentoId, arquivo);
    setEnviando(false);
    if (!r.ok) {
      setErro(r.erro);
      return;
    }
    // Relê do banco em vez de presumir o estado novo: é ele que decide, e uma
    // tela que se adianta mente quando a RPC recusa por um motivo que ela não
    // previu.
    const d = await lerMeuPagamento(documentoId);
    setDados(d);
    setArquivo(null);
    aoMudar();
  }

  const esperandoPagamento = dados?.estado === "aguardando_pagamento";
  const emAnalise = dados?.estado === "comprovante_em_analise";

  return (
    <dialog
      ref={ref}
      onClose={aoFechar}
      onClick={(e) => {
        if (e.target === ref.current) aoFechar();
      }}
      aria-labelledby="titulo-pagamento"
      data-modal="pagamento"
      className="m-auto max-h-[90vh] w-[min(94vw,34rem)] overflow-y-auto rounded-[1.125rem] bg-superficie p-0 text-tinta shadow-2xl backdrop:bg-azul/70 backdrop:backdrop-blur-sm"
    >
      <div className="flex items-start justify-between gap-4 border-b border-borda p-6">
        <div>
          <h2 id="titulo-pagamento" className="font-display text-xl font-bold text-titulo">
            Pagamento do documento
          </h2>
          <p className="mt-1 text-sm text-cinza">{nomeDoTipo}</p>
        </div>
        <button
          onClick={aoFechar}
          aria-label="Fechar"
          className="grid size-11 shrink-0 place-items-center rounded-full text-cinza transition hover:bg-chip hover:text-titulo"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            className="size-5"
            aria-hidden="true"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="p-6">
        {carregando && <p className="text-sm text-cinza">Carregando…</p>}

        {!carregando && !dados && (
          <p className="text-sm text-cinza">
            Não conseguimos carregar o pagamento deste pedido. Recarregue a página.
          </p>
        )}

        {!carregando && dados && (
          <>
            {/* ── A RECUSA anterior, quando houver ──────────────────────────
                Família ALERTA (laranja), nunca `aviso` — nesta casa `aviso` é
                VERDE, a família de confirmação, e um aviso verde diz "está tudo
                bem", que é o oposto da mensagem. E `alerta` tem `-fundo` e
                `-tinta`, mas NÃO tem `-borda`. */}
            {dados.recusa && (
              <div
                data-recusa
                className="mb-5 rounded-lg border border-alerta-tinta/40 bg-alerta-fundo p-3"
              >
                <p className="text-sm font-semibold text-alerta-tinta">
                  O comprovante anterior foi recusado
                </p>
                <p className="mt-1 text-sm text-tinta">{dados.recusa}</p>
              </div>
            )}

            {emAnalise && (
              <p data-estado="em-analise" className="text-sm text-tinta">
                O seu comprovante está em análise pela secretaria. Assim que ele for
                validado, o documento entra na fila de emissão.
              </p>
            )}

            {esperandoPagamento && (
              <>
                {/* ── 1. Pagar ─────────────────────────────────────────────
                    Destino de FORA, então `<a target="_blank">` com o
                    `rel="noreferrer"` e o `sr-only` dizendo que abre em outra
                    aba — é a regra do `LinkDoCard`, e o motivo é que a página
                    de destino não é nossa. */}
                <ol className="space-y-5">
                  <li>
                    <p className="text-sm font-semibold text-titulo">
                      1. Pague a taxa
                    </p>
                    {dados.link ? (
                      <a
                        href={dados.link}
                        target="_blank"
                        rel="noreferrer"
                        data-link-pagamento
                        className="pill-cta mt-2 inline-flex items-center gap-2"
                      >
                        Abrir a página de pagamento
                        <span className="sr-only">(abre em outra aba)</span>
                      </a>
                    ) : (
                      // Sem link não há como pagar, e dizer "pague" sem o
                      // caminho é o defeito que este popup existe para
                      // consertar. Então a tela manda falar com a secretaria —
                      // e diz que o problema não é do aluno.
                      <p className="mt-1 text-sm text-cinza">
                        O link de pagamento deste documento ainda não foi cadastrado.
                        Fale com a secretaria — está tudo certo do seu lado.
                      </p>
                    )}
                  </li>

                  {/* ── 2. Anexar ────────────────────────────────────────── */}
                  <li>
                    <p className="text-sm font-semibold text-titulo">
                      2. Anexe o comprovante
                    </p>
                    <p className="mt-1 text-sm text-cinza">
                      PDF, JPG ou PNG. A secretaria confere e libera o documento.
                    </p>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                      onChange={(e) => {
                        setArquivo(e.target.files?.[0] ?? null);
                        setErro(null);
                      }}
                      data-campo="comprovante"
                      className="mt-2 block w-full cursor-pointer rounded-lg border border-borda bg-fundo p-2 text-sm text-tinta file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-chip file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-titulo"
                    />
                    <button
                      onClick={mandar}
                      disabled={enviando || !arquivo}
                      data-acao="enviar-comprovante"
                      // `pill-desligada` e não `opacity-*`: desabilitado que o
                      // aluno olha tentando entender pede cor própria, e a
                      // opacidade sobre o CTA laranja dá 3,33 : 1 no claro e
                      // 1,92 sobre navy (medido em 01/09).
                      className={
                        enviando || !arquivo
                          ? "pill-desligada mt-3 inline-flex"
                          : "pill-cta mt-3 inline-flex"
                      }
                    >
                      {enviando ? "Enviando…" : "Enviar comprovante"}
                    </button>
                  </li>
                </ol>

                {erro && (
                  <p
                    data-erro
                    className="mt-4 rounded-lg border border-erro-tinta/40 bg-erro-fundo p-3 text-sm text-erro-tinta"
                  >
                    {erro}
                  </p>
                )}
              </>
            )}

            {/* ── O histórico ────────────────────────────────────────────────
                Existe porque a recusa é um LAÇO: na segunda recusa a pergunta do
                aluno é "o que eu já mandei?". Sem a lista ele não sabe se o
                envio de ontem chegou. */}
            {dados.comprovantes.length > 0 && (
              <div className="mt-6 border-t border-borda pt-4">
                <p className="text-sm font-semibold text-titulo">
                  Comprovantes que você enviou
                </p>
                <ul data-historico className="mt-2 space-y-2">
                  {dados.comprovantes.map((c) => (
                    <li key={c.seq} className="text-sm">
                      <span className="text-cinza">
                        {new Date(c.enviado_em).toLocaleDateString("pt-BR")}
                      </span>{" "}
                      <span
                        className={
                          c.situacao === "aprovado"
                            ? "font-semibold text-aviso-tinta"
                            : c.situacao === "recusado"
                              ? "font-semibold text-alerta-tinta"
                              : "font-semibold text-cinza"
                        }
                      >
                        {c.situacao === "aprovado"
                          ? "aprovado"
                          : c.situacao === "recusado"
                            ? "recusado"
                            : "em análise"}
                      </span>
                      {c.motivo && <span className="text-tinta"> — {c.motivo}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </dialog>
  );
}
